import { Button, Field, Input } from "@bafe/ui";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Navigate, useSearchParams } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { api } from "../../lib/api";
import { useCompleteSignIn } from "../../lib/auth-flow";
import { peekPendingSignIn } from "../../lib/stores";

export function TwoFactor() {
	const [searchParams] = useSearchParams();
	const next = searchParams.get("next");
	const completeSignIn = useCompleteSignIn();
	const pending = peekPendingSignIn();

	const [mode, setMode] = useState<"totp" | "recovery">("totp");
	const [code, setCode] = useState("");

	const verify = useMutation({
		mutationFn: () => {
			if (!pending) throw new Error("Start again from the sign-in screen.");
			const trimmed = code.trim().replace(/\s+/g, "");
			return api.login({
				email: pending.email,
				password: pending.password,
				...(mode === "totp" ? { totpCode: trimmed } : { recoveryCode: trimmed }),
			});
		},
		onSuccess: (session) => completeSignIn(session, next ?? "/"),
	});

	// Reaching this screen without a sign-in attempt means a reload or a
	// bookmark; there is nothing to verify against.
	if (!pending) return <Navigate to="/signin" replace />;

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		verify.mutate();
	}

	return (
		<AuthShell
			title="Enter your verification code"
			description={
				mode === "totp"
					? "Open your authenticator app and enter the six-digit code for Key."
					: "Enter one of the recovery codes you saved when you turned on two-factor authentication."
			}
		>
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				<Field label={mode === "totp" ? "Six-digit code" : "Recovery code"} required>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							value={code}
							required
							autoFocus
							inputMode={mode === "totp" ? "numeric" : "text"}
							autoComplete="one-time-code"
							autoCapitalize="none"
							spellCheck={false}
							aria-describedby={describedBy}
							aria-invalid={invalid}
							className="text-dns"
							onChange={(event) => setCode(event.target.value)}
						/>
					)}
				</Field>

				{verify.isError && <ErrorPanel error={verify.error} />}

				<Button type="submit" variant="primary" fullWidth pending={verify.isPending} pendingLabel="Checking…">
					Verify and sign in
				</Button>

				<Button
					variant="ghost"
					fullWidth
					onClick={() => {
						setMode(mode === "totp" ? "recovery" : "totp");
						setCode("");
						verify.reset();
					}}
				>
					{mode === "totp" ? "Use a recovery code instead" : "Use my authenticator app instead"}
				</Button>
			</form>
		</AuthShell>
	);
}
